import type { CSSProperties, ReactNode } from 'react';

import classNames from 'classnames';

import { parseLocaleInteger } from '../../utils/number';

import { useDreamComponentsContext } from '../../dreamComponentProvider';
import { SignupGDPRCompliance } from '../../signupElement/SignupGDPRCompliance';
import type {
  BasicNode,
  DreamRenderElementProps,
  Price,
  StripeCardElementComponent,
  StripePaymentElementComponent,
  Tier,
} from '../../types';
import { formHelper, formLabel, groupSubsFormSummary, input } from '../Pricing.css';
import CheckoutPaymentSection from './CheckoutPaymentSection';

interface Props {
  email?: string;
  onEmailChange: (value: string) => void;
  groupName?: string;
  onGroupNameChange: (value: string) => void;
  seatCount: number | null;
  onSeatCountChange: (value: number | null) => void;
  taxId: string;
  onTaxIdChange: (value: string) => void;
  ctaText: string;
  isCtaDisabled: boolean;
  showTaxIdField: boolean;
  StripeCardElement?: StripeCardElementComponent;
  StripePaymentElement?: StripePaymentElementComponent;
  selectedTier?: Tier;
  selectedPrice?: Price;
  PaymentRequestButtonElement?: ReactNode;
  hasTermsOfService?: boolean;
  hasCustomTermsOfService?: string;
  customTermsOfServiceText?: string;
  inputTextColor?: CSSProperties['color'];
  inputPlaceholderColor?: CSSProperties['color'];
  inputBackgroundColor?: CSSProperties['backgroundColor'];
  isInternalCheckout: boolean;
  translations?: DreamRenderElementProps<BasicNode>['translations'];
}

const GroupCheckoutForm = ({
  email,
  onEmailChange,
  groupName,
  onGroupNameChange,
  seatCount,
  onSeatCountChange,
  taxId,
  onTaxIdChange,
  isCtaDisabled,
  StripeCardElement,
  StripePaymentElement,
  selectedTier,
  selectedPrice,
  ctaText = 'Upgrade',
  showTaxIdField = false,
  PaymentRequestButtonElement,
  hasTermsOfService = false,
  hasCustomTermsOfService,
  customTermsOfServiceText,
  inputTextColor,
  inputPlaceholderColor,
  inputBackgroundColor,
  isInternalCheckout,
  translations,
}: Props) => {
  const { language = 'en-US' } = useDreamComponentsContext();

  if (!selectedTier || !selectedPrice) {
    return null;
  }

  const emailField = (
    <div className="flex flex-col gap-1">
      <label htmlFor="email" className={classNames(formLabel)}>
        {translations?.['upgrade.tabs.group.form.purchaser_email']}
      </label>
      <input
        name="email"
        defaultValue={email}
        onBlur={(e) => e.currentTarget.value && onEmailChange(e.currentTarget.value)}
        type="email"
        placeholder="example@acme.com"
        className={classNames('py-2 px-4', input)}
      />
    </div>
  );

  const groupNameField = (
    <div className="flex flex-col gap-1">
      <label htmlFor="group_name" className={classNames(formLabel)}>
        {translations?.['upgrade.tabs.group.form.group_name']}
      </label>
      <input
        name="group_name"
        value={groupName || ''}
        onChange={(e) => onGroupNameChange(e.currentTarget.value)}
        type="text"
        placeholder="Acme.com"
        className={classNames('py-2 px-4', input)}
      />
    </div>
  );

  const seatsField = (
    <div className="flex flex-col gap-1">
      <label htmlFor="group_purchase_quantity_display" className={classNames(formLabel)}>
        {translations?.['upgrade.tabs.group.form.seats']}
      </label>
      <input type="hidden" name="group_purchase_quantity" value={seatCount ?? ''} />
      <input
        id="group_purchase_quantity_display"
        value={seatCount !== null ? seatCount.toLocaleString(language) : ''}
        onChange={(e) => {
          const raw = e.currentTarget.value;
          if (raw.trim() === '') {
            onSeatCountChange(null);
            return;
          }
          // Locale-aware integer parse: strip the user's grouping separator
          // (US comma, FR thin space, DE period…) and truncate at the
          // locale's decimal — so "1,234.56" (en-US) and "1 234,56" (fr-FR)
          // both yield 1234, never 123456. Invalid input returns null,
          // which we treat as "keep prior state."
          const parsed = parseLocaleInteger(raw, language);
          if (parsed !== null) {
            onSeatCountChange(parsed);
          }
        }}
        type="text"
        inputMode="numeric"
        placeholder="100"
        aria-describedby="group-purchase-quantity-help"
        className={classNames('py-2 px-4', input)}
      />
      <p id="group-purchase-quantity-help" className={classNames('text-left', formHelper)}>
        {translations?.['upgrade.tabs.group.form.minimum_seats']}
      </p>
    </div>
  );

  const summaryLine = translations?.['upgrade.tabs.group.form.summary'] ? (
    <p className={groupSubsFormSummary}>{translations['upgrade.tabs.group.form.summary']}</p>
  ) : null;

  const fields = (
    <>
      {emailField}
      {groupNameField}
      {seatsField}
      {summaryLine}
    </>
  );

  if (!StripePaymentElement && PaymentRequestButtonElement) {
    return (
      <div className="flex flex-col gap-6 max-w-md w-full">
        {fields}
        {PaymentRequestButtonElement}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 max-w-md w-full">
      {fields}
      <CheckoutPaymentSection
        isInternalCheckout={isInternalCheckout}
        StripeCardElement={StripeCardElement}
        StripePaymentElement={StripePaymentElement}
        inputTextColor={inputTextColor}
        inputPlaceholderColor={inputPlaceholderColor}
        inputBackgroundColor={inputBackgroundColor}
        showTaxIdField={showTaxIdField}
        taxId={taxId}
        onTaxIdChange={onTaxIdChange}
        gdprField={
          hasTermsOfService ? (
            <SignupGDPRCompliance
              hasCustomTermsOfService={hasCustomTermsOfService}
              customTermsOfServiceText={customTermsOfServiceText}
              defaultTOSConsentValue={true}
            />
          ) : null
        }
        ctaText={ctaText}
        isCtaDisabled={isCtaDisabled}
      />
    </div>
  );
};

export default GroupCheckoutForm;
